import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Divider, Spin } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { siteConfig, loading: configLoading } = useSiteConfig();
  const navigate = useNavigate();

  const onFinish = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const success = await login(values.username, values.password);
      if (success) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderLogo = () => {
    if (configLoading) {
      return (
        <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <Spin size="small" />
        </div>
      );
    }

    if (siteConfig?.logo_url) {
      return (
        <img
          src={siteConfig.logo_url}
          alt="Logo"
          className="w-20 h-20 rounded-full mx-auto mb-6 object-contain"
        />
      );
    }

    return (
      <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
        <span className="text-white font-bold text-3xl">G</span>
      </div>
    );
  };

  const renderSiteInfo = () => {
    if (configLoading) {
      return (
        <>
          <Title level={2} className="mb-2 text-center">
            <Spin size="small" /> 加载中...
          </Title>
          <Text type="secondary" className="text-center block">
            正在加载系统配置...
          </Text>
        </>
      );
    }

    return (
      <>
        <Title level={2} className="mb-2 text-center">
          欢迎回来
        </Title>
        <Text type="secondary" className="text-center block">
          {siteConfig?.site_name ? `登录到${siteConfig.site_name}` : '登录到游戏服务器控制面板'}
        </Text>
        {siteConfig?.site_description && (
          <Text type="secondary" className="text-center block mt-2 text-sm">
            {siteConfig.site_description}
          </Text>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <Card className="w-full max-w-md shadow-2xl">
        <div className="text-center mb-8">
          {renderLogo()}
          {renderSiteInfo()}
        </div>

        <Form
          name="login"
          onFinish={onFinish}
          size="large"
          autoComplete="off"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名或邮箱' }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名或邮箱"
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="w-full h-12 rounded-lg font-medium"
            >
              登录
            </Button>
          </Form.Item>

          <Form.Item>
            <div className="text-right">
              <Link to="/forgot-password" className="text-blue-500 hover:text-blue-600 text-sm">
                忘记密码？
              </Link>
            </div>
          </Form.Item>
        </Form>

        <Divider>或</Divider>

        <div className="text-center">
          <Text type="secondary">还没有账户？</Text>
          <Link to="/register" className="ml-2 text-blue-500 hover:text-blue-600">
            立即注册
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default Login;